import { useRemoteUsers } from 'agora-rtc-react';
import { useEffect, useMemo, useRef } from 'react';

import { useMeetingData } from '@/app/queries/MeetingQuery/context';

const STOP_FEATURE_DELAY = 180000; // 3 minutes in milliseconds

export interface CallFeature {
  isActive: boolean;
  start(): void;
  stop(): void;
}

export default function useAutoCallFeature(
  { joined, isEnabled }: { joined: boolean; isEnabled: boolean },
  feature: CallFeature,
) {
  const remoteUsers = useRemoteUsers();
  const { participants } = useMeetingData();

  const knownUids = useMemo(() => new Set(
    participants.flatMap(p => [p.user_id, p.screen_share_id])
  ), [participants]);

  const realRemoteUsers = useMemo(
    () => remoteUsers.filter(u => knownUids.has(u.uid as number)),
    [remoteUsers, knownUids]
  );
  const stopTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!joined || !isEnabled || !feature.isActive) {
      return;
    }
    if (realRemoteUsers.length === 0) {
      stopTimeoutRef.current = setTimeout(() => {
        feature.stop();
      }, STOP_FEATURE_DELAY);
    } else {
      if (stopTimeoutRef.current) {
        clearTimeout(stopTimeoutRef.current);
        stopTimeoutRef.current = null;
      }
    }

    return () => {
      if (stopTimeoutRef.current) {
        clearTimeout(stopTimeoutRef.current);
      }
    };
  }, [joined, isEnabled, feature, realRemoteUsers.length]);

  useEffect(function triggerFeature() {
    if (!isEnabled) return;
    if (joined) {
      if (realRemoteUsers.length && !feature.isActive) {
        feature.start();
      }
    } else {
      feature.stop();
    }
  }, [joined, isEnabled, feature, realRemoteUsers.length]);
}
